import { Component, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import moment from 'moment/moment';
import { Moment } from 'moment';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { MetricGroup } from './metric-table.model';
import { StandupDashboardServiceService } from '../standup-dashboard-service.service';
import { taskStatuses } from '../../../planner/planner-table/planner-table.component';
import { TaskServiceService } from '../../../planner/task-service.service';
import {
  ActionCategoryType, CriticalOrderStagesGQL,
  DashboardType,
  MeFieldsFragment,
  MetricGroupAssignmentType,
  MetricGroupHighlightWhen,
  MetricValueCommentTypeConnection,
  TaskStatus,
  TaskType, UnEscalateMetricValueFromDashboardsGQL, UpdateCriticalStageGQL,
  UpdateOperationsTaskMutationVariables
} from '../../../../generated/graphql';
import { UserprofileService } from '../../../shared/userprofile.service';
import { PusherService } from '../../../pusher/pusher.service';
import { ReasonChipsData } from '../../../shared/reason-with-comment/reason-with-comment.component';
import { NewTaskDialogComponent } from '../../../planner/new-task-dialog/new-task-dialog.component';
import { FilterInterface } from '../../../shared/filter-bar/filter-bar.component';
import {
  TaskMultipleCreateDialogComponent
} from '../task-multiple-create-dialog/task-multiple-create-dialog.component';
import { ProblemDialogComponent } from './problem-dialog/problem-dialog.component';
import { RelayIdService } from '../../../shared/relay-id.service';
import { NotificationService } from '../../../shared/notification.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-metric-table',
  templateUrl: './metric-table.component.html',
  styleUrls: ['./metric-table.component.scss']
})
export class MetricTableComponent implements OnInit, OnDestroy {
  private pusherSubscription: Subscription;
  Object = Object;
  statuses = taskStatuses;
  moment = moment;
  metricGroups: MetricGroup[] = [];
  displayedColumns: string[] = [
    'metricGroup', 'selection', 'isImportant', 'escalate', 'isWorkis', 'isChannelEmail', 'pm', 'client', 'measureValue', 'goal',
    'reasonComment', 'orderStage', 'actionCategory', 'taskComment', 'taskStatus', 'taskNumericPlan', 'taskDate', 'assignedTaskPm',
    'actionCategoryL2', 'taskCommentL2', 'taskStatusL2', 'taskDateL2', 'assignedTaskPmL2'
  ];
  tableData: any[] = [];
  metricCounts: { [key: string]: { total: number, important: number } } = { };
  user: MeFieldsFragment;
  selectedNextActions: { [key: string]: { l1: string, l2: string } } = { };
  nextTaskComments: { [key: string]: { l1: string, l2: string } } = { };
  categories;
  initDates;
  filterCache: FilterInterface;
  isDatePickerClosed: boolean;
  isLoading = true;
  selectedRowId: string;
  metricSelectionDict: { [key: string]: any } = { };
  onlyImportantShow: boolean;
  beData: any;
  regularData: any[];
  escalatedData: any[];
  metricGoalVsValueSum: {[key: string]: { goalSum: number, valueSum: number }} = {};
  metricLateToBeWithEmployeeSum: {[key: string]: { planSum: number }} = {};
  metricLateToBeWithEmployee: {[key: string]: {[key: string]: { plan: number, taskid: string[] }}} = {};
  tasks: any[];
  comments: any[];
  deletedMetricsCounts: {[key: string]: number} = {};
  escalatedNow: {metricId: string, level: string, dashboardId: string}[] = [];
  isEscalating: boolean = false;
  isTasksLoading: boolean = true;
  parentDashboardId: string;
  stages = [];

  @Input() dashboard: string;
  @Input() cardTitle: string;
  @Input() cardDescription: string;
  @Input() dashboardItem: DashboardType;
  @Input() escalationDashboard: DashboardType;
  @Input() escalationDashboards: DashboardType[] = [];
  @Input() metricGroupForOtherProblemId: string;
  @Input() cardId: any;
  @Input() includeFromChildDashboards: boolean;

  constructor(
    private dashboardService: StandupDashboardServiceService,
    private taskService: TaskServiceService,
    private userprofileService: UserprofileService,
    private dialog: MatDialog,
    private pusherService: PusherService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private notify: NotificationService,
    private translateService: TranslateService,
    public relayIdService: RelayIdService,
    private unEscalate: UnEscalateMetricValueFromDashboardsGQL,
    private criticalOrderStagesGQL: CriticalOrderStagesGQL,
    private updateCriticalStageGQL: UpdateCriticalStageGQL
  ) { }

  ngOnInit() {
    this.getCriticalStages();
    this.loadUserData();
    this.connectPusherTaskUpdate();

    const lastWorkingDay = this.getLastWorkingDay(moment());
    this.initDates = {
      dateRange: {
        dateFrom: lastWorkingDay,
        dateTo: moment()
      }
    };
    this.route.queryParams.subscribe(params => {
      const paramDateFrom = params['dateFrom'];
      const paramDateTo = params['dateTo'];
      this.selectedRowId = params['selectedMetric'];
      if (paramDateFrom && paramDateTo) {
        this.initDates = {
          dateRange: {
            dateFrom: moment(paramDateFrom),
            dateTo: moment(paramDateTo)
          }
        };
      }
    });

    // Handle child dashboards if includeFromChildDashboards is true
    if (this.includeFromChildDashboards && this.dashboardItem.meetingLevel !== null) {
      // We're on a child dashboard (lvl1, lvl2, or lvl3), need to find the parent (lvl0: meetingLevel === null)
      this.dashboardService.getDashboards().pipe(take(1)).subscribe(allDashboards => {
        const parentDashboard = allDashboards.find(dashboard =>
          dashboard.meetingLevel === null &&
          dashboard.regionGroup?.id === this.dashboardItem.regionGroup?.id
        ) || this.dashboardItem;

        this.parentDashboardId = parentDashboard.id;
      });
    }
  }

  getCriticalStages() {
    this.criticalOrderStagesGQL.fetch().subscribe(v => {
      this.stages = v.data.criticalOrderStages.map(val => ({
        id: val.id,
        title: val.title,
        sortOrder: val.sortOrder
      }));
    });
  }

  updateCriticalStage(element, stage) {
    this.updateCriticalStageGQL.mutate({ input: {id: element?.id, criticalOrderStage: stage?.id} }).subscribe(v => {
      element.criticalOrderStage = {
        ...element.criticalOrderStage,
        id: stage?.id,
        title: stage?.title,
        sortOrder: stage?.sortOrder
      };
    });
  }

  onUpdateTableData(data) {
    if (data?.reason) {
      this.tableData = this.tableData.map(v => {
        if (v?.reasonSet) {
          if (v.reasonSet.find(value => value.id === data.reason.id)) {
            return {
              ...v,
              reasonSet: v.reasonSet.map(value => {
                const isTarget = value.reasonGroup.find(target => target.id === data.reason.id);
                if (isTarget) {
                  return {
                    ...value,
                    metricClientReason: { ...data.metricClientReason },
                    reasonGroup: value.reasonGroup.map(group => {
                      if (isTarget) {
                        return data.reason;
                      }
                    })
                  };
                }
              })
            };
          } else {
            return { ...v };
          }
        } else {
          return { ...v };
        }
      });
    }
  }

  openProblemTaskDialog() {
    const config: MatDialogConfig<any> = {
      data: {
        metricGroupForOtherProblemId: this.metricGroupForOtherProblemId
      },
      panelClass: 'rounded-dialog-16',
      width: '400px',
      autoFocus: false
    };

    this.dialog.open(ProblemDialogComponent, config).afterClosed().subscribe(value => {
      if (value) {
        this.onFilterChanges(this.filterCache, true);
        this.snackBar.open(
          this.translate.instant('CREATED_SUCCESSFULLY'), null, { duration: 5000, horizontalPosition: 'left' }
        );
      }
    });
  }

  loadMetrics(dateFrom?: string, dateTo?: string) {
    this.isLoading = true;

    if (this.includeFromChildDashboards && this.parentDashboardId && this.parentDashboardId !== this.dashboardItem.id) {
      forkJoin({
        parentRegular: this.dashboardService.getMetricTableValues(this.cardId, this.parentDashboardId, dateFrom, dateTo).pipe(take(1)),
        currentRegular: this.dashboardService.getMetricTableValues(this.cardId, this.dashboardItem.id, dateFrom, dateTo).pipe(take(1)),
        currentEscalated: this.dashboardService.getMetricValuesEscalated(this.dashboardItem.id, this.dashboardItem.id, dateFrom, dateTo).pipe(take(1))
      }).subscribe(data => {
        // Combine regular data from both dashboards and escalated from current dashboard only
        this.regularData = [...data.parentRegular, ...data.currentRegular];
        this.escalatedData = data.currentEscalated;

        if (this.regularData && this.escalatedData) {
          let combinedData = this.combineMultiSourceData([...this.escalatedData, ...this.regularData]);

          this.beData = combinedData;
          this.prepareTableData(this.beData);
        }
        this.isLoading = false;
      }, () => this.isLoading = false);
    } else {
      // Normal flow - query from current dashboard only
      forkJoin({
        regular: this.dashboardService.getMetricTableValues(this.cardId, this.dashboardItem.id, dateFrom, dateTo).pipe(take(1)),
        escalated: this.dashboardService.getMetricValuesEscalated(this.dashboardItem.id, this.dashboardItem.id, dateFrom, dateTo).pipe(take(1))
      }).subscribe(data => {
        this.regularData = data.regular;
        this.escalatedData = data.escalated;

        if (this.regularData && this.escalatedData) {
          let combinedData = this.combineMultiSourceData([...this.escalatedData, ...this.regularData]);

          this.beData = combinedData;
          this.prepareTableData(this.beData);
        }
        this.isLoading = false;
      }, () => this.isLoading = false);
    }
    // this.dashboardService.getMetricTableValues(this.cardId, this.dashboardItem.id, dateFrom, dateTo)
    //   .subscribe(
    //     data => {
    //       this.regularData = data;
    //
    //       if (this.regularData && this.escalatedData) {
    //         this.beData = this.combineMultiSourceData([...this.escalatedData, ...this.regularData]);
    //         this.prepareTableData(this.beData);
    //       }
    //     },
    //     () => this.isLoading = false
    //   );
    // this.dashboardService.getMetricValuesEscalated(this.dashboardItem.id, this.dashboardItem.id, dateFrom, dateTo)
    //   .subscribe(
    //     data => {
    //       this.escalatedData = data;
    //
    //       if (this.regularData && this.escalatedData) {
    //         this.beData = this.combineMultiSourceData([...this.escalatedData, ...this.regularData]);
    //         this.prepareTableData(this.beData);
    //       }
    //     },
    //     () => this.isLoading = false
    //   );
  }

  combineMultiSourceData(data: MetricGroupAssignmentType[]) {
    const newData: MetricGroupAssignmentType[] = [];
    data.forEach(edge => {
      const groupObj = newData.find(item => item.metricGroup.id === edge.metricGroup.id);
      if (!groupObj) {
        newData.push(edge);
      } else {
        groupObj.metricGroup.metricvalueSet.edges.push(...edge.metricGroup.metricvalueSet.edges);
      }
    });

    return newData;
  }

  loadUserData() {
    this.userprofileService.getMe().subscribe(profile => this.user = profile.data?.me);
  }

  connectPusherTaskUpdate() {
    this.pusherSubscription = this.pusherService.dashboardTaskUpdateEmitter.subscribe(event => {
      const metricObj = this.tableData.find(item => item.nextTask?.id === event.task_id);
      const metricObjL2 = this.tableData.find(item => item.nextTaskLevel2?.id === event.task_id);

      if (metricObj) {
        this.dashboardService.getUpdatedTask(event.task_id).subscribe(task => {
          metricObj.nextTask = task;
          this.nextTaskComments[metricObj.id].l1 = task.comment;
        });
      } else if (metricObjL2) {
        this.dashboardService.getUpdatedTask(event.task_id).subscribe(task => {
          metricObjL2.nextTaskLevel2 = task;
          this.nextTaskComments[metricObjL2.id].l2 = task.comment;
        });
      }
    });
  }

  selectRow(id: string) {
    if (this.selectedRowId === id) {
      this.selectedRowId = null;
    } else if (id !== undefined) {
      this.selectedRowId = id;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        selectedMetric: this.selectedRowId
      },
      queryParamsHandling: 'merge'
    });
  }

  onFilterChanges(filters: FilterInterface, force?: boolean) {
    this.dashboardService.changedMetricDatesSubject.next(filters.dateRange);
    const dateRange = filters.dateRange;
    const cachedRange = this.filterCache?.dateRange;

    this.isDatePickerClosed = !!(
      !this.filterCache || (dateRange.dateFrom.isSame(cachedRange.dateFrom) && (!cachedRange.dateTo && dateRange.dateTo))
    );

    this.filterCache = filters;

    if ((this.isDatePickerClosed || force) && dateRange && (dateRange.dateFrom && dateRange.dateTo)) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          dateFrom: dateRange.dateFrom ? moment(dateRange.dateFrom).format('YYYY-MM-DD') : null,
          dateTo: dateRange.dateTo ? moment(dateRange.dateTo).format('YYYY-MM-DD') : null
        },
        queryParamsHandling: 'merge'
      });
      this.loadMetrics(
        dateRange.dateFrom ? moment(dateRange.dateFrom).format('YYYY-MM-DD') : null,
        dateRange.dateTo ? moment(dateRange.dateTo).format('YYYY-MM-DD') : null
      );
    }
  }

  metricGvVCalculator(metricName: string, metcirGoal: number, metricValue: number) {
    if (this.metricGoalVsValueSum[metricName]) {
      this.metricGoalVsValueSum[metricName].goalSum += metcirGoal;
      this.metricGoalVsValueSum[metricName].valueSum += metricValue;
    } else {
      this.metricGoalVsValueSum[metricName] = {
        goalSum: metcirGoal,
        valueSum: metricValue
      };
    }
  }

  metricPlanCalculator(metricName: string, metricPlan: number, task: any) {
    const metricEntry = this.metricLateToBeWithEmployee[metricName] || {};
    if (metricEntry[task.id]) {
      metricEntry[task.id].plan = metricPlan;
    } else {
      metricEntry[task.id] = {
        plan: metricPlan,
        taskid: [task.id]
      };
    }
    this.metricLateToBeWithEmployee[metricName] = metricEntry;
  }

  getTasksById(metricTasks, metricGroups, metricValuesIdsForComments) {
    if (metricTasks.length > 0) {
      this.isTasksLoading = true;
      this.dashboardService.getTasksByIdQuery(metricTasks).subscribe(
        data => {
          this.tasks = data.tasks.edges;
          if (this.tasks) {
            metricGroups.forEach(group => {
              const metricGroup = group.metricGroup;
              const metrics = metricGroup.metricvalueSet.edges.map(edge => edge.node);
              metrics.forEach(metric => {
                this.tasks.map( task => {
                  if (task.node.id === btoa(`TaskType:${metric.nextTaskId}`)) {
                    this.tableData.map( metricInTable => {
                      if (metricInTable.id === metric.id) {
                        metricInTable.nextTask = task.node;
                        metricInTable.nextTaskDate = metric.nextTask ? metric.nextTask.date : null;
                        metricInTable.nextTaskCompletedBy = metric.nextTask && metric.nextTask.completedBy ? metric.nextTask.completedBy.fullName : null;
                        if (!!this.getPlannedCustomFieldValue(metricInTable.nextTask)) {
                          this.metricPlanCalculator(metricGroup.title, this.getPlannedCustomFieldValue(metricInTable.nextTask), metricInTable.nextTask);
                          this.calculateMetricSums();
                        }
                        if (this.nextTaskComments[metric.id] && this.nextTaskComments[metric.id].l2) {
                          this.nextTaskComments[metric.id] = {
                            l1: task.node?.comment || '',
                            l2: this.nextTaskComments[metric.id].l2
                          };
                        } else {
                          this.nextTaskComments[metric.id] = {
                            l1: task.node?.comment || '',
                            l2: ''
                          };
                        }
                        if (this.selectedNextActions[metric.id] && this.selectedNextActions[metric.id].l2) {
                          this.selectedNextActions[metric.id] = {
                            l1: task.node?.nextActionCategory?.id || '',
                            l2: this.selectedNextActions[metric.id].l2
                          };
                        } else {
                          this.selectedNextActions[metric.id] = {
                            l1: task.node?.nextActionCategory?.id || '',
                            l2: ''
                          };
                        }
                      }
                    });
                  }
                  if (task.node.id === btoa(`TaskType:${metric.nextTaskLevel2Id}`)) {
                    this.tableData.map( metricInTable => {
                      if (metricInTable.id === metric.id) {
                        metricInTable.nextTaskLevel2 = task.node;
                        if (this.nextTaskComments[metric.id] && this.nextTaskComments[metric.id].l1) {
                          this.nextTaskComments[metric.id] = {
                            l1: this.nextTaskComments[metric.id].l1,
                            l2: task.node?.comment || ''
                          };
                        } else {
                          this.nextTaskComments[metric.id] = {
                            l1: '',
                            l2: task.node?.comment || ''
                          };
                        }
                        if (this.selectedNextActions[metric.id] && this.selectedNextActions[metric.id].l1) {
                          this.selectedNextActions[metric.id] = {
                            l1: this.selectedNextActions[metric.id].l1,
                            l2: task.node?.nextActionCategory?.id || ''
                          };
                        } else {
                          this.selectedNextActions[metric.id] = {
                            l1: '',
                            l2: task.node?.nextActionCategory?.id || ''
                          };
                        }
                      }
                    });
                  }
                });
              });
            });
          }
          this.isLoading = false;
          this.isTasksLoading = false;
        },
        () => {
          this.isLoading = false;
          this.isTasksLoading = false;
        }
      );
    }
    if (metricValuesIdsForComments.length > 0) {
      this.dashboardService.getCommentsByIdQuery(metricValuesIdsForComments).subscribe(
        data => {
          this.comments = data.metricValueComments.edges;
          if (this.comments) {
            metricGroups.forEach(group => {
              const metricGroup = group.metricGroup;
              const metrics = metricGroup.metricvalueSet.edges.map(edge => edge.node);
              metrics.forEach(metric => {
                this.comments.map(comment => {
                  if (metric.metricvaluecommentSet.edges?.length) {
                    const metricCommentIdx = metric.metricvaluecommentSet.edges.findIndex(({ node }) => node.id === comment.node.id);
                    if (metricCommentIdx > -1) {
                      this.tableData.map(metricInTable => {
                        if (metricInTable.id === metric.id) {
                          metricInTable.metricvaluecommentSet.edges[metricCommentIdx].node = comment.node;
                          metricInTable.reasonSet = this.dashboardService.groupCommentsByReasonSet(metricInTable.metricvaluecommentSet);
                        }
                      });
                    }
                  }
                });
              });
            });
          }
          this.isLoading = false;
        },
        () => this.isLoading = false
      );
    }
  }

  prepareTableData(metricGroups) {
    const metricTasks = [];
    const metricValuesIdsForComments = [];
    // console.log('metricGroups', metricGroups);
    this.tableData = [];
    this.metricGoalVsValueSum = {};
    this.metricLateToBeWithEmployeeSum = {};
    this.metricLateToBeWithEmployee = {};
    this.metricCounts = { };
    if (!metricGroups) {
      return;
    }

    metricGroups.forEach(group => {
      const metricGroup = group.metricGroup;
      metricGroup.nextActions?.edges.sort((x, y) => x.node.title > y.node.title ? 1 : -1);
      if (metricGroup && metricGroup.metricvalueSet && metricGroup.metricvalueSet.edges) {
        const metrics = metricGroup.metricvalueSet.edges.map(edge => edge.node);
        metrics.sort((a, b) => {
          if (a.manager?.fullName < b.manager?.fullName) {
            return -1;
          }
          if (a.manager?.fullName > b.manager?.fullName) {
            return 1;
          }

          if (a.client?.name < b.client?.name) {
            return -1;
          }
          if (a.client?.name > b.client?.name) {
            return 1;
          }

          if (a.id < b.id) {
            return -1;
          }
          if (a.id > b.id) {
            return 1;
          }

          return 0;
        });

        let dataSetIdx = 0;

        if (metrics.length === 0) {
          metrics.push({
            id: 'empty',
            groupId: metricGroup.id,
            groupName: metricGroup.title,
            groupDescription: metricGroup.description,
            date: null,
            measureValue: null,
            goal: null,
            workis: null,
            managerName: null,
            nextTaskDate: null,
            nextTaskCompletedBy: null,
            taskCategory: metricGroup.taskCategory,
            nextActions: metricGroup.nextActions?.edges.map(item => item.node),
            customFieldCategory: metricGroup.taskCategory,
            highlightWhen: metricGroup.highlightWhen,
            isTotalRow: false,
            metricvaluecommentSet: { edges: [] },
            isEmptyGroup: true
          });
        }

        const importantMetrics = metrics.filter(metric => metric.isImportant);
        metrics.forEach((metric, index) => {
          if (metric.nextTaskId) {
            metricTasks.push(btoa(`TaskType:${metric.nextTaskId}`));
          }
          if (metric.nextTaskLevel2Id) {
            metricTasks.push(btoa(`TaskType:${metric.nextTaskLevel2Id}`));
          }
          if (metric.metricvaluecommentSet.edges[0]) {
            metricValuesIdsForComments.push(metric.id);
          }
          // console.log('metric', metric);
          if (!this.onlyImportantShow || this.onlyImportantShow && metric.isImportant) {
            this.tableData.push({
              ...metric,
              groupId: metricGroup.id,
              groupName: metricGroup.title,
              groupDescription: metricGroup.description,
              isGroupRow: dataSetIdx === 0,
              groupSpan: 1 + (this.onlyImportantShow ? importantMetrics.length : metrics.length),
              date: metric.date,
              measureValue: metric.value,
              goal: metric.goal,
              workis: metric.workis,
              managerName: metric.manager ? metric.manager.fullName : null,
              nextTaskDate: metric.nextTask ? metric.nextTask.date : null,
              nextTaskCompletedBy: metric.nextTask && metric.nextTask.completedBy ? metric.nextTask.completedBy.fullName : null,
              taskCategory: metricGroup.taskCategory,
              nextActions: metricGroup.nextActions?.edges.map(item => item.node),
              customFieldCategory: metricGroup.taskCategory,
              highlightWhen: metricGroup.highlightWhen,
              isTotalRow: false,
              fromEscalated: metric.escalatedFrom?.id === this.dashboardItem.id
            });
            if (metrics.length - 1 === index) {
              this.tableData.push({
                groupName: metricGroup.title,
                groupId: metricGroup.id,
                isTotalRow: true,
                isEmptyGroup: metrics[0].isEmptyGroup
              });
            }
            this.metricGvVCalculator(metricGroup.title, metric.goal, metric.value);
            this.selectedNextActions[metric.id] = {
              l1: '',
              l2: ''
            };
            this.nextTaskComments[metric.id] = {
              l1: '',
              l2: ''
            };
            if (!this.metricCounts[metricGroup.id]) {
              this.metricCounts[metricGroup.id] = { total: 0, important: 0 };
            }
            if (metric.isImportant) {
              this.metricCounts[metricGroup.id].important++;
            }
            this.metricCounts[metricGroup.id].total++;
            dataSetIdx++;
          }
        });
      }
    });
    this.tableData.map(metric => {
      metric.escalations?.edges.map(item => {
        const targetDashboard = this.escalationDashboards?.find(d => d.meetingLevel === item.node?.escalatedTo?.meetingLevel);
        const input = {
          metricId: metric?.id,
          level: item.node?.escalatedTo?.meetingLevel,
          dashboardId: targetDashboard?.id,
          escalationId: item?.node?.id,
          escalatedTo: item?.node?.escalatedTo?.id
        };
        this.escalatedNow.push(input);
      });
    });
    this.getTasksById(metricTasks, metricGroups, metricValuesIdsForComments);
  }

  isMeasureValueExceeded(metric): boolean {
    if (this.isTasksLoading) {
      return false;
    }
    if (metric.highlightWhen === MetricGroupHighlightWhen.GreaterThan) {
      return metric.measureValue > metric.goal && !metric.nextTask;
    } else if (metric.highlightWhen === MetricGroupHighlightWhen.LessThan) {
      return metric.measureValue < metric.goal && !metric.nextTask;
    }
  }

  isTaskStatusChangeDisabled(task: any): boolean {
    return task.status === 'DONE' && (task.schedules?.edges.length || task.createdBySchedule);
  }

  onDateChange(date: Moment, oldDate: string, task: TaskType) {
    const newDate = moment(date).format('YYYY-MM-DD');
    if (oldDate !== newDate) {
      this.updateTask({ taskId: task.id, date: newDate });
    }
  }

  onStatusChange(status: TaskStatus, task: TaskType) {
    this.updateTask({ taskId: task.id, status });
  }

  onNextActionChange(
    event: MatSelectChange,
    nextActions: ActionCategoryType[],
    metricId: string,
    taskCategory: any,
    level: string,
    clientId: string,
    candidateId: string,
    elementToUpdate: any
  ) {
    const preparedClientId = this.relayIdService.convertRelayTypes(clientId, 'CompanyModelType');
    const proceedMetric = this.tableData.find(metric => metric.id === metricId);
    const createCompletedTask = nextActions.find(i => i.id === event.value).createCompletedTask;
    const action = nextActions.find(x => x.id === event.value).taskCategory;

    if (proceedMetric[level]) {
      this.updateTask({
        taskId: proceedMetric[level].id,
        nextActionCategory: event.value,
        category: action.id || taskCategory.id || null
      });
    } else {
      const dialog = this.dialog.open(NewTaskDialogComponent, {
        data: {
          nextActionCategory: event.value,
          createCompletedTask,
          category: action || taskCategory || null,
          user: this.user,
          clientId: preparedClientId,
          candidateId,
          dashboardId: this.dashboardItem.id,
          refetchQueries: 'GetOperationsTasks'
        },
        panelClass: 'rounded-dialog-16',
        width: '600px',
        height: '640px',
        autoFocus: false
      });

      dialog.afterClosed().subscribe(value => {
        if (value) {
          if (level === 'nextTask') {
            this.dashboardService.setMetricValueNextTask(metricId, value.id).subscribe(
              resp => {
                this.snackBar.open(this.translate.instant('CREATED_SUCCESSFULLY'), null, { duration: 5000, horizontalPosition: 'left' });
                elementToUpdate.nextTask = resp.data.updateMetricValue.metricValue.nextTask;
                // this.metricPlanCalculator(elementToUpdate.groupName, this.getPlannedCustomFieldValue(elementToUpdate.nextTask), elementToUpdate.nextTask);
                // this.calculateMetricSums();
                this.nextTaskComments[elementToUpdate.id].l1 = elementToUpdate.nextTask.comment;
              },
              () => { }
            );
          } else if (level === 'nextTaskLevel2') {
            this.dashboardService.setMetricValueNextTaskL2(metricId, value.id).subscribe(
              resp => {
                this.snackBar.open(this.translate.instant('CREATED_SUCCESSFULLY'), null, { duration: 5000, horizontalPosition: 'left' });
                elementToUpdate.nextTaskLevel2 = resp.data.updateMetricValue.metricValue.nextTaskLevel2;
                this.nextTaskComments[elementToUpdate.id].l2 = elementToUpdate.nextTaskLevel2.comment;
              },
              () => { }
            );
          }
          this.dashboardService.changedTaskSubject.next(value);
        } else {
          if (level === 'nextTask') {
            this.selectedNextActions[metricId].l1 = '';
          } else if (level === 'nextTaskLevel2') {
            this.selectedNextActions[metricId].l2 = '';
          }
        }
      });
    }
  }

  onSelectedProfilesChange($event: string[], task: TaskType) {
    this.updateTask({ taskId: task.id, completedBy: $event });
  }

  onCommentChange(metric: any, level: string, levelKey: string) {
    if (this.nextTaskComments[metric.id][level] !== metric[levelKey].comment) {
      this.updateTask({ taskId: metric[levelKey].id, comment: this.nextTaskComments[metric.id][level] });
    }
  }

  updateTask(updatesTask: UpdateOperationsTaskMutationVariables) {
    this.taskService.updateTaskPartial(updatesTask).subscribe(
      data => {
        this.dashboardService.changedTaskSubject.next(data.data.updateOperationsTask.task);
      }, () => { }
    );
  }

  openTaskDialog(element: any, taskLvl) {
    const task = element[taskLvl];
    const config: MatDialogConfig<any> = {
      data: { task: null },
      panelClass: 'rounded-dialog-16',
      width: '600px',
      height: '640px',
      autoFocus: false
    };

    if (task?.id) {
      this.taskService.getTask(task.id).subscribe(resp => {
        config.data.task = resp.data.task;
        this.location.replaceState(`/standups/${encodeURIComponent(task.id)}`);
        this.dialog.open(NewTaskDialogComponent, config).afterClosed().subscribe(value => {
          if (value?.delete) {
            // const plannedCustomField = task?.customFieldValues?.edges.find(({ node }) =>
            //   node.customField.fieldType.name.includes('plan')
            // );
            //
            // // if (plannedCustomField) {
            // //   for (const metricName in this.metricLateToBeWithEmployee) {
            // //     const tasks = this.metricLateToBeWithEmployee[metricName];
            // //     if (tasks[task.id]) {
            // //       delete this.metricLateToBeWithEmployee[metricName][task.id];
            // //       this.calculateMetricSums();
            // //     }
            // //   }
            // // }

            this.snackBar.open(
              this.translate.instant('DELETED_SUCCESSFULLY'), null, { duration: 5000, horizontalPosition: 'left' }
            );
            element[taskLvl] = null;
            this.selectedNextActions[element.id][taskLvl === 'nextTask' ? 'l1' : 'l2'] = '';
          } else if (value?.id) {
            const plannedCustomField = task?.customFieldValues?.edges.find(({ node }) =>
              node.customField.fieldType.name.includes('plan')
            );

            if (plannedCustomField) {
              for (const metricName in this.metricLateToBeWithEmployee) {
                const tasks = this.metricLateToBeWithEmployee[metricName];
                // if (tasks[task.id]) {
                //   this.metricLateToBeWithEmployee[metricName][task.id].plan = this.taskService.getCustomPlanValue();
                //   this.calculateMetricSums();
                // }
              }
            }

            this.dashboardService.changedTaskSubject.next(value);

            this.snackBar.open(
              this.translate.instant('UPDATED_SUCCESSFULLY'), null, { duration: 5000, horizontalPosition: 'left' }
            );
          }

          this.location.replaceState('/standups');
        });
      });
    }
  }

  getLastWorkingDay(date) {
    let lastWorkingDay;

    switch (date.isoWeekday()) {
      case 1:
        lastWorkingDay = date.subtract(3, 'days');
        break;
      case 6:
        lastWorkingDay = date.subtract(1, 'days');
        break;
      case 7:
        lastWorkingDay = date.subtract(2, 'days');
        break;
      default:
        lastWorkingDay = date.subtract(1, 'days');
        break;
    }

    return lastWorkingDay;
  }

  openDescription(template: TemplateRef<any>) {
    this.dialog.open(template, {
      autoFocus: false,
      maxHeight: '80dvh'
    });
  }

  ngOnDestroy() {
    if (this.pusherSubscription) {
      this.pusherSubscription.unsubscribe();
    }
  }

  changeImportance(element) {
    this.dashboardService.updateMetricValueImportance(element.id, !element.isImportant).subscribe(
      re => {
        element.isImportant = re.data.updateMetricValue.metricValue.isImportant;
        if (element.isImportant) {
          this.metricCounts[element.groupId].important++;
        } else {
          this.metricCounts[element.groupId].important--;
        }

        this.beData.forEach(group => {
          group.metricGroup.metricvalueSet.edges.forEach(value => {
            if (value.node.id === element.id) {
              value.node.isImportant = element.isImportant;
            }
          });
        });
      }
    );
  }

  escalate(metricValue, dashboardToEscalateTo = this.escalationDashboard) {
    const level = dashboardToEscalateTo.meetingLevel || 'II';
    this.isEscalating = true;
    if (!this.isEscalatedToLevel(metricValue, dashboardToEscalateTo.meetingLevel || 'II')) {
      this.dashboardService.escalateMetricValue({
        id: metricValue.id,
        escalatedFrom: this.dashboardItem.id,
        escalatedTo: dashboardToEscalateTo.id
      }).subscribe(res => {
        this.notify.notify(this.translateService.instant(
          'METRIC_SUCCESS_ESCALATION',
          { dashboard: res.data.updateMetricValue.metricValue?.escalatedTo.title || '' }
        ));
        metricValue = {...metricValue, escalations: {...metricValue.escalations, edges: [...res.data?.updateMetricValue.metricValue.escalations.edges]}};
        const input = {metricId: metricValue.id, level, dashboardId: dashboardToEscalateTo.id};
        res.data?.updateMetricValue.metricValue.escalations.edges.map(v => {
          if (v.node.metricValue.id === metricValue.id) {
            if (v.node.escalatedTo.meetingLevel === level) {
              input['escalationId'] = v.node.id;
            }
          }
        });
        metricValue.escalatedTo = dashboardToEscalateTo;
        this.escalatedNow.push({metricId: metricValue.id, level, dashboardId: this.dashboardItem.id});
        this.isEscalating = false;
      });
    } else {
      let targetId;
      this.escalatedNow.map(v => {
        if (metricValue.id === v.metricId && v.level === level) {
          targetId = v['escalationId'];
        }
      })
      if (targetId) {
        this.unEscalate.mutate({escalationId: targetId}).subscribe(val => {
          if (val.data.unEscalateMetricValueFromDashboards.ok) {
            this.escalatedNow = this.escalatedNow.filter(v =>
              v.metricId !== val.data.unEscalateMetricValueFromDashboards.metricValue.id || v.level !== level);
            metricValue = {...metricValue, escalations: {...metricValue.escalations, edges: metricValue.escalations.edges.filter(item => item.node.id !== targetId)}};
            this.isEscalating = false;
          }
          this.isEscalating = false;
        });
      }
    }
  }

  escalateToLevel(metricValue, level) {
    this.isEscalating = true;
    if (!this.isEscalatedToLevel(metricValue, level)) {
      const targetDashboard = this.escalationDashboards?.find(d => d.meetingLevel === level);

      if (!targetDashboard) {
        this.isEscalating = false;
        return;
      }

      // Check if already escalated to this specific dashboard
      if (this.isEscalatedToLevel(metricValue, level)) {
        this.isEscalating = false;
        return;
      }

      // Use the new API for multi-dashboard escalation
      this.dashboardService.escalateMetricValueToDashboards(
        metricValue.id,
        [targetDashboard.id],
        this.dashboardItem.id
      ).subscribe(res => {
        if (res.data?.escalateMetricValueToDashboards?.metricValue) {
          this.notify.notify(this.translateService.instant(
            'METRIC_SUCCESS_ESCALATION',
            { dashboard: targetDashboard.title || '' }
          ));
          // Add both the metric ID, level, and dashboard ID to track which dashboard it was escalated to
          metricValue = {...metricValue, escalations: {...metricValue.escalations, edges: [...res.data?.escalateMetricValueToDashboards?.metricValue.escalations.edges]}};
          const input = {
            metricId: metricValue.id,
            level,
            dashboardId: targetDashboard.id
          };
          res.data.escalateMetricValueToDashboards.metricValue.escalations.edges.map(v => {
            if (
              v.node.metricValue.id === metricValue.id &&
              v.node.escalatedTo.meetingLevel === level &&
              v.node.escalatedTo.id === targetDashboard.id
            ) {
              input['escalationId'] = v.node.id;
              input['escalatedTo'] = v.node.escalatedTo.id;
            }
          });
          this.escalatedNow.push(input);
          this.isEscalating = false;
        } else if (res.data?.escalateMetricValueToDashboards?.errors?.length) {
          // Fall back to old API if new one returns errors
          this.escalate(metricValue, targetDashboard);
        }
      }, error => {
        // Fall back to old API if new one fails completely
        this.escalate(metricValue, targetDashboard);
      });
    } else {
      let targetId;
      const targetDashboard = this.escalationDashboards?.find(d => d.meetingLevel === level);
      this.escalatedNow.map(v => {
        if (metricValue.id === v.metricId && v.level === level && v['escalatedTo'] === targetDashboard.id) {
          targetId = v['escalationId'];
        }
      })
      if (targetId) {
        this.unEscalate.mutate({escalationId: targetId}).subscribe(val => {
          if (val.data.unEscalateMetricValueFromDashboards.ok) {
            this.escalatedNow = this.escalatedNow.filter(v =>
              v.metricId !== val.data.unEscalateMetricValueFromDashboards.metricValue.id || v.level !== level);
            metricValue = {...metricValue, escalations: {...metricValue.escalations, edges: metricValue.escalations.edges.filter(item => item.node.id !== targetId)}};
            this.isEscalating = false;
          }
          this.isEscalating = false;
        });
      }
    }
  }

  isEscalatedToLevel(element, level) {
    // if (!element) return false;
    //
    // // Get the target dashboard for this level
    const targetDashboard = this.escalationDashboards?.find(d => d.meetingLevel === level);
    // if (!targetDashboard?.id) return false;
    //
    // // Check if element was recently escalated to this specific dashboard in this session
    const recentlyEscalated = this.escalatedNow.some(item =>
      item.metricId === element.id && item.level === level && item['escalatedTo'] === targetDashboard.id
    );

    if (recentlyEscalated) {
      return true;
    }

    // // Check if element has any escalations to this specific dashboard in the new model
    // if (element.escalations?.edges?.length > 0) {
    //   return element.escalations.edges.some(edge =>
    //     edge.node.escalatedTo?.id === targetDashboard.id
    //   );
    // }
    //
    // // Check legacy model
    // if (element.escalatedTo) {
    //   return element.escalatedTo.id === targetDashboard.id;
    // }

    return false;
  }

  hasDashboardWithLevel(level: string): boolean {
    // Check new model first
    if (this.escalationDashboards?.length) {
      return this.escalationDashboards.some(d => d.meetingLevel === level);
    }

    // Fallback to old model
    if (this.escalationDashboard) {
      return this.escalationDashboard.meetingLevel === level;
    }

    return false;
  }

  getPlannedCustomFieldValue(task: TaskType): undefined | number {
    const plannedCustomField = task?.customFieldValues?.edges.find(
      ({ node }) => node.customField.fieldType.name.includes('plan')
    );
    if (plannedCustomField) {
      return plannedCustomField.node.numericValue;
    } else {
      return undefined;
    }
  }

  calculateMetricSums(): {[key: string]: { planSum: number }} {
    const metricLateToBeWithEmployeeSum: {[key: string]: { planSum: number }} = {};

    for (const metricName in this.metricLateToBeWithEmployee) {
      const tasks = this.metricLateToBeWithEmployee[metricName];
      let planSum = 0;
      for (const taskId in tasks) {
        planSum += tasks[taskId].plan;
      }
      metricLateToBeWithEmployeeSum[metricName] = { planSum };
    }

    return this.metricLateToBeWithEmployeeSum = metricLateToBeWithEmployeeSum;
  }


  onSelectionChange(element) {
    if (this.metricSelectionDict[element.id]) {
      delete this.metricSelectionDict[element.id];
    } else {
      this.metricSelectionDict[element.id] = element;
    }
  }

  createMultipleTasks() {
    const selectedMetricsIds = Object.keys(this.metricSelectionDict);
    let availableActions = [];

    selectedMetricsIds.forEach(id => {
      this.metricSelectionDict[id].nextActions?.forEach(action => {
        if (action.createCompletedTask) {
          availableActions.push(action);
        }
      });
    });

    availableActions = availableActions.filter(action =>
      availableActions.filter(({ id }) => id === action.id).length === selectedMetricsIds.length
    );

    const uniqueActions = Array
      .from(new Set(availableActions.map(item => item.id)))
      .map(id => availableActions.find(item => item.id === id));

    const uniqueMetricValues = selectedMetricsIds
      .filter(id => this.metricSelectionDict[id].nextActions.some(action => uniqueActions.some(item => item.id === action.id)))
      .map(id => this.metricSelectionDict[id]);

    this.dialog.open(TaskMultipleCreateDialogComponent, {
      data: {
        uniqueActions,
        uniqueMetricValues,
        dashboardId: this.dashboardItem.id,
        user: this.user
      }
    }).afterClosed().subscribe(resp => {
      if (resp) {
        this.snackBar.open(this.translate.instant('CREATED_SUCCESSFULLY'), null, { duration: 5000, horizontalPosition: 'left' });
        this.onFilterChanges(this.filterCache, true);
        this.metricSelectionDict = { };
      }
    });
  }

  onlyImportantVisibilityChange() {
    this.prepareTableData(this.beData);
  }

  formatInnerText(text: string): string {
    return text.replace(/\n/g, '<br>');
  }

  removeMetricValueFromTable(metricValue) {
    let plugIdx;
    let plug;


    this.tableData = this.tableData.filter((item, idx) => {

      if (item.id === metricValue.id && this.tableData[idx - 1].isTotalRow === true && this.tableData[idx + 1].isTotalRow === true) {
        plugIdx = idx;
        plug = {
          id: 'empty',
          groupId: item.groupId,
          groupName: item.groupName,
          groupDescription: item.groupDescription,
          date: null,
          measureValue: null,
          goal: null,
          workis: null,
          managerName: null,
          nextTaskDate: null,
          nextTaskCompletedBy: null,
          taskCategory: item.taskCategory,
          nextActions: item.nextActions,
          customFieldCategory: item.customFieldCategory,
          highlightWhen: item.highlightWhen,
          isTotalRow: false,
          metricvaluecommentSet: { edges: [] },
          isEmptyGroup: true,
          isGroupRow: true,
          groupSpan: 2
        };
        return true;
      } else {
        return item.id !== metricValue.id;
      }
    });

    if (plugIdx) {
      this.tableData[plugIdx] = plug;
    }

    if (!plugIdx) {
      this.deletedMetricsCounts[metricValue.groupId] = (this.deletedMetricsCounts[metricValue.groupId] || 0) + 1;
      this.metricCounts[metricValue.groupId].total = this.metricCounts[metricValue.groupId].total - 1;
    }
  }
}
